
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

import TranscriptFilters from '@/modules/BillingModule/components/TranscriptFilters/index.vue'
import TranscriptGeneration from '@/modules/BillingModule/components/TranscriptGeneration/index.vue'

import { DataState } from '@/core/classes/DataState.class'

import { BillingGetTranscriptsParams, TranscriptT } from '@/core/types/billing.types'

import { billing } from '@/core/realisations/billing.realisation'

import { BillingTranscriptsColumns } from '@/core/constants/columns/BillingTranscripts.columns'

// composable
import useAccount from '@/common/composable/useAccount'
import { useMaska } from '@/common/composable/useMaska'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'table-scroll-wrapper': TableScrollWrapper,
        'the-table': TheTable,
        'transcript-filters': TranscriptFilters,
        'transcript-generation': TranscriptGeneration,
    },
    setup() {
        const router = useRouter()

        const { havePermissions } = useAccount()
        const state = reactive(new DataState<TranscriptT>({}))
        const { numberWithSpaces } = useMaska()
        const notifications = useNotifications()

        const filters_state = reactive({
            limit: 50,
            //
            companyId: undefined as string | undefined,
            actNumber: undefined as string | undefined,
            actFrom1CCreatedAtFrom: undefined as string | undefined,
            actFrom1CCreatedAtTo: undefined as string | undefined,
        })

        const loadTranscripts = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const params: BillingGetTranscriptsParams = {
                    page,
                    limit: filters_state.limit,
                    actNumber: filters_state.actNumber,
                    actFrom1CCreatedAtFrom: filters_state.actFrom1CCreatedAtFrom,
                    actFrom1CCreatedAtTo: filters_state.actFrom1CCreatedAtTo,
                    companyId: filters_state.companyId,
                }

                const response = await billing.getTranscripts(params)
                state.list = response.data
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadTranscripts(state.pagination.current_page)
        }

        onBeforeMount(() => {
            loadTranscripts(1)
        })

        const applyFilters = (params: BillingGetTranscriptsParams) => {
            filters_state.companyId = params.companyId || undefined
            filters_state.actNumber = params.actNumber

            filters_state.actFrom1CCreatedAtFrom =
                params.actFrom1CCreatedAtFrom === '' ? undefined : params.actFrom1CCreatedAtFrom

            filters_state.actFrom1CCreatedAtTo =
                params.actFrom1CCreatedAtTo === '' ? undefined : params.actFrom1CCreatedAtTo

            loadTranscripts(1)
        }

        const resetFilters = () => {
            filters_state.companyId = undefined
            filters_state.actNumber = undefined
            filters_state.actFrom1CCreatedAtFrom = undefined
            filters_state.actFrom1CCreatedAtTo = undefined

            loadTranscripts(1)
        }

        const getStats = (key: string, row: TranscriptT) => {
            if (key === 'approvers') {
                return `${row.countOfApproveSigners} / ${row.totalCountOfApprovers}`
            } else if (key === 'signers') {
                return `${row.countOfSignatoriesSigners} / ${row.totalCountOfSignatories}`
            }
        }

        const getClass = (key: string, row: TranscriptT) => {
            if (key === 'approvers') {
                return row.countOfApproveSigners === row.totalCountOfApprovers &&
                    row.totalCountOfApprovers
                    ? 'text-green-500'
                    : 'text-yellow-500'
            } else if (key === 'signers') {
                return row.countOfSignatoriesSigners === row.totalCountOfSignatories &&
                    row.totalCountOfSignatories
                    ? 'text-green-500'
                    : 'text-yellow-500'
            }
        }

        const isSignersOrApprovers = (key: string) => {
            return key === 'approvers' || key === 'signers'
        }

        const generation_state = reactive({
            is_opened: false,
        })

        const onGenerateTranscript = () => {
            generation_state.is_opened = true
        }

        const onCloseGenerateTranscript = () => {
            generation_state.is_opened = false
        }

        const onTranscriptGenerated = () => {
            onCloseGenerateTranscript()
            loadTranscripts(state.pagination.current_page)
        }

        const onUpdateTranscript = (transcript: TranscriptT) => {
            router.push(`/billing/transcripts/${transcript.id}/update`)
        }

        const deleteTranscript = async (transcript_id: number) => {
            const answer = confirm('Вы уверены, что хотите удалить транскрипт?')
            if (!answer || !transcript_id) return

            try {
                state.is_loading = true

                await billing.deleteTranscript(transcript_id)

                notifications.notify({ title: 'Транскрипт успешно удален!', type: 'success' })
                loadTranscripts(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При удалении транскрипта произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            BillingTranscriptsColumns,
            havePermissions,
            state,
            filters_state,
            loadTranscripts,
            applyFilters,
            resetFilters,
            getStats,
            getClass,
            isSignersOrApprovers,
            //
            numberWithSpaces,
            //
            generation_state,
            onGenerateTranscript,
            onCloseGenerateTranscript,
            onTranscriptGenerated,
            onUpdateTranscript,
            deleteTranscript,
            onLimitChange,
            formatDate,
        }
    },
})
